import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Button } from '@components';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

import {
    nhanVienLoadFormDataAction,
    nhanVienCreateAction
} from '@app/store/reducers/nhanVien';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { VAI_TRO_OPTIONS } from '@app/store/constants';
import { toast } from 'react-toastify';
import { messageToast } from '@app/utils/define';

const NhanVienAdd = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { nguoiDungs, boPhans, teams } = useSelector(
        (state) => state.nhanVien
    );

    const [nguoiDungId, setNguoiDungId] = useState('');
    const [boPhanId, setBoPhanId] = useState('');
    const [teamId, setTeamId] = useState('');
    const [vaiTro, setVaiTro] = useState(VAI_TRO_OPTIONS[0].value);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        dispatch(nhanVienLoadFormDataAction());
    }, [dispatch]);

    const filteredTeams = useMemo(() => {
        if (!boPhanId) return teams || [];
        return (teams || []).filter(
            (t) => String(t.boPhanId) === String(boPhanId)
        );
    }, [teams, boPhanId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!nguoiDungId) {
            toast.error('Vui lòng chọn Người dùng.');
            return;
        }
        if (!boPhanId) {
            toast.error('Vui lòng chọn Bộ phận.');
            return;
        }

        const model = {
            nguoiDungId,
            boPhanId: Number(boPhanId),
            teamId: teamId ? Number(teamId) : null,
            vaiTro: Number(vaiTro)
        };

        try {
            setSubmitting(true);
            await dispatch(nhanVienCreateAction(model)).unwrap();

            const successMsg =
                (messageToast && messageToast.messageToastCreateSuccess) ||
                'Thêm nhân viên thành công';

            toast.success(successMsg);
            history.push(URI_INTERNAL.NHANVIEN_PATH);
        } catch (err) {
            console.error('Thêm nhân viên lỗi:', err);

            let msg =
                (err && err.message) ||
                (typeof err === 'string' ? err : '') ||
                (err && err.title) ||
                '';

            if (!msg && err && err.errors) {
                msg = Object.values(err.errors).flat().join('; ');
            }

            const errorBaseMsg =
                (messageToast && messageToast.messageToastCreateError) ||
                'Thêm nhân viên thất bại';

            toast.error(msg ? `${errorBaseMsg}: ${msg}` : errorBaseMsg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <Card.Header as="h5">Thêm nhân viên</Card.Header>
            <Card.Body>
                <Form noValidate onSubmit={handleSubmit}>
                    {/* Người dùng */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Người dùng <span className="text-danger">*</span>
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="select"
                                value={nguoiDungId}
                                onChange={(e) =>
                                    setNguoiDungId(e.target.value)
                                }
                            >
                                <option value="">
                                    -- Chọn người dùng --
                                </option>
                                {(nguoiDungs || []).map((nd) => (
                                    <option key={nd.id} value={nd.id}>
                                        {nd.hoTen}
                                        {nd.email ? ` (${nd.email}` : ''}
                                        {nd.sdt
                                            ? ` - ${nd.sdt}`
                                            : nd.email
                                            ? ')'
                                            : ''}
                                    </option>
                                ))}
                            </Form.Control>
                            {(!nguoiDungs || nguoiDungs.length === 0) && (
                                <small className="text-muted">
                                    Hiện chưa có Người dùng nào chưa được gán
                                    Nhân viên.
                                </small>
                            )}
                        </Col>
                    </Form.Group>

                    {/* Bộ phận */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Bộ phận <span className="text-danger">*</span>
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="select"
                                value={boPhanId}
                                onChange={(e) => {
                                    setBoPhanId(e.target.value);
                                    setTeamId('');
                                }}
                            >
                                <option value="">
                                    -- Chọn bộ phận --
                                </option>
                                {(boPhans || []).map((bp) => (
                                    <option key={bp.id} value={bp.id}>
                                        {bp.tenBoPhan || bp.name}
                                    </option>
                                ))}
                            </Form.Control>
                        </Col>
                    </Form.Group>

                    {/* Team */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Tổ / Nhóm
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="select"
                                value={teamId}
                                onChange={(e) => setTeamId(e.target.value)}
                                disabled={!boPhanId}
                            >
                                <option value="">
                                    -- Không thuộc team --
                                </option>
                                {filteredTeams.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.tenTeam || t.name}
                                    </option>
                                ))}
                            </Form.Control>
                            {!boPhanId && (
                                <small className="text-muted">
                                    Hãy chọn Bộ phận trước để lọc Team.
                                </small>
                            )}
                        </Col>
                    </Form.Group>

                    {/* Vai trò */}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Vai trò <span className="text-danger">*</span>
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="select"
                                value={vaiTro}
                                onChange={(e) =>
                                    setVaiTro(Number(e.target.value))
                                }
                            >
                                {VAI_TRO_OPTIONS.map((opt) => (
                                    <option
                                        key={opt.value}
                                        value={opt.value}
                                    >
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Control>
                        </Col>
                    </Form.Group>

                    {/* Buttons */}
                    <Form.Group as={Row} className="mb-3">
                        <Col md={{ span: 10, offset: 2 }}>
                            <ButtonGroup>
                                <Button
                                    type="submit"
                                    theme="primary"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Đang lưu...' : 'Lưu'}
                                </Button>
                                &nbsp;&nbsp;&nbsp;
                                <Button
                                    type="button"
                                    theme="secondary"
                                    onClick={() =>
                                        history.push(
                                            URI_INTERNAL.NHANVIEN_PATH
                                        )
                                    }
                                    disabled={submitting}
                                >
                                    Hủy
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Form.Group>
                </Form>

                <ReactSpinner loadings={submitting} />
            </Card.Body>
        </Card>
    );
};

export default NhanVienAdd;
